import { useStore, Lead } from '../store/useStore';
import { callExternalAPI } from './ai/api-router';
import { BotResponse } from './ai/types';
import { TaskExecutor } from './local-ai';

export async function listAvailableModels(apiKey: string) {
  try {
    const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`);
    if (!res.ok) throw new Error(`Status ${res.status}`);
    const data = await res.json();
    return data.models || [];
  } catch (err) {
    console.error('Failed to list models:', err);
    return [];
  }
}

export async function generateLeadInsight(lead: Lead): Promise<string> {
  const status = String(lead.status);
  const isHot = status === 'negotiating' || status === 'qualified';
  const statusNote = status === 'new' ? 'Fast follow-up recommended.' :
                     status === 'contacted' ? 'Keep momentum with a touchpoint.' :
                     isHot ? 'Active deal - prioritize this lead.' : 'Monitor for future opportunities.';
  const valNote = lead.estimatedValue > 250000 ? ` High-value target ($${lead.estimatedValue.toLocaleString()}).` : '';
  
  return `🤖 OS Bot: ${statusNote}${valNote} Review their notes and reach out when ready.`;
}

/**
 * Legacy Wrappers (v13.0 Support)
 * These ensure backward compatibility for old pages/tests.
 */
export async function createTask(params: any) {
  return TaskExecutor.execute('task_action', { action: 'create_task', ...params });
}

export async function updateLeadStatusViaAI(leadId: string, newStatus: string) {
  const res = await TaskExecutor.execute('crm_action', { 
    action: 'update_status', 
    leadId, 
    newStatus 
  });
  return { success: res.success, message: res.message };
}

export async function sendSMSViaAI(target: string, message: string, carrier?: string) {
  const res = await TaskExecutor.execute('comms_action', { 
    action: 'send_sms', 
    target, 
    message, 
    targetCarrier: carrier 
  });
  return { success: res.success, message: res.message };
}

export async function generatePageInsights(url: string, context: any, signal?: AbortSignal): Promise<string[]> {
  const prompt = `Analyze this page: ${url}. Context: ${JSON.stringify(context)}. Provide 3 individual strategic insights as a list. Each insight should be a separate line.`;
  const res = await callExternalAPI(prompt, { ...context, silent: true }, signal);
  const response = res.response;
  
  if (!response) return ["No insights available at this time."];
  
  // Split by newline and clean up numbering/bullets
  return response
    .split('\n')
    .map(line => line.replace(/^(\d+\.|-|\*)\s*/, '').trim())
    .filter(line => line.length > 0)
    .slice(0, 3);
}

export interface CallScriptTemplate {
  name: string;
  category: string;
  script: string;
  description: string;
}

export async function generateCallScriptTemplates(_lead: Lead): Promise<CallScriptTemplate[]> {

  const defaults: CallScriptTemplate[] = [
    {
      name: "Introductory Outreach",
      category: "Introductory",
      description: "A straightforward, professional opening to gauge initial interest.",
      script: `Hi {{lead.name}}, my name is [Your Name] and I'm a local real estate investor. I was calling about your property at {{lead.address}}. Are you open to receiving a quick, no-obligation cash offer for it?`
    },
    {
      name: "Follow-up Check-in",
      category: "Follow-up",
      description: "Use this when reconnecting with a lead who wasn't ready before or needs a nudge.",
      script: `Hey {{lead.name}}, it's [Your Name] following up regarding {{lead.address}}. I know timing is everything in real estate, so I just wanted to see if your timeline has changed or if you're ready to explore an offer.`
    },
    {
      name: "Urgent Closer",
      category: "Urgent/Closer",
      description: "A more direct script for leads showing high motivation or distressed properties.",
      script: `Hello {{lead.name}}, this is [Your Name]. I'm looking to close on a property in your neighborhood this month, and {{lead.address}} caught my eye. If I pay cash and cover all closing costs, what's a number that would make sense for you?`
    }
  ];

  let customScripts: CallScriptTemplate[] = [];
  try {
    const saved = localStorage.getItem('user_custom_scripts');
    if (saved) customScripts = JSON.parse(saved);
  } catch (err) { console.error(err); }

  return [...defaults, ...customScripts];
}

/**
 * Primary entry point for all OS Bot Intelligence (v14.0).
 * Implements a "Neural Hierarchy": 
 * 1. Exact Coded match
 * 2. Fuzzy Learned match
 * 3. Smart Neural (Cloud/Local LLM)
 * 4. Recursive Teaching (Smart -> Coded)
 */
export async function processPrompt(
  prompt: string,
  context: any = {},
  modelId: string = 'gemini-2.0-flash-lite',
  signal?: AbortSignal
): Promise<BotResponse> {
  try {
    const store = useStore.getState();
    const userId = store.currentUser?.id;
    const preferredProvider = store.preferred_api_provider || 'local';

    // 1. ROUTING DECISION
    let effectiveModel = modelId || preferredProvider || 'gemini-2.0-flash-lite';
    
    // If user preference is local, it should override the default cloud model
    // unless a specific non-default model was requested.
    if ((preferredProvider === 'local' || preferredProvider === 'os-bot') && (!modelId || modelId === 'gemini-2.0-flash-lite')) {
      effectiveModel = preferredProvider;
    }

    const isLocalMode = effectiveModel === 'local' || effectiveModel === 'os-bot';

    console.log(`[OS Bot Pipeline] 🛣️ Routing Decision:`, {
      preferred: preferredProvider,
      requested: modelId,
      resolved: effectiveModel,
      isLocal: isLocalMode
    });

    // Load local utilities
    const { recognizeIntent } = await import('./local-ai/intent-engine');
    const { TaskExecutor } = await import('./local-ai/task-executor');
    const { generateResponse } = await import('./local-ai/response-generator');
    
    const nluResult = await recognizeIntent(prompt);

    // 2.0 GREETING INTERCEPTOR (v15.0): Handle small talk/greetings locally immediately.
    // This prevents cloud models from over-interpreting simple greetings like "yo" or "hello" 
    // as lead search commands or tool calls.
    if (nluResult && (nluResult.intent.name === 'greeting' || nluResult.intent.name === 'small_talk')) {
      console.log(`[OS Bot Pipeline] 👋 Greeting Intercept:`, nluResult.intent.name);
      const executionResult = await TaskExecutor.execute(nluResult.intent.name, nluResult.params);
      const personality = store.aiTone || 'Professional';
      
      return {
        intent: nluResult.intent.name,
        response: generateResponse(nluResult.intent, executionResult, prompt, personality),
        data: executionResult.data,
        systemLog: `🤖 OS Bot · Local`
      };
    }

    // 2. IF LOCAL MODE: Handle locally
    if (isLocalMode && nluResult && nluResult.confidence >= 90 && !nluResult.needsAgentLoop) {
      const intentName = nluResult.intent.name;
      console.log(`[OS Bot Pipeline] 🧠 Local Match:`, intentName);
      
      const executionResult = await TaskExecutor.execute(intentName, nluResult.params);
      const personality = store.aiTone || 'Professional';
      
      store.incrementAiUsage(1, false);
      
      return {
        intent: intentName,
        response: generateResponse(nluResult.intent, executionResult, prompt, personality),
        data: executionResult.data,
        systemLog: `🤖 OS Bot`
      };
    }
    
    // 2.5 AGENT LOOP OVERRIDE
    if (nluResult?.needsAgentLoop && userId) {
      console.log(`[OS Bot Pipeline] ⚡ Agent Loop Triggered:`, nluResult.intent.name);
      const { callAgentLoop } = await import('./agent/agent-client');
      const agentRes = await callAgentLoop(userId, prompt);
      
      if (agentRes.handled) {
        // Execute tool calls sequentially
        const { TaskExecutor } = await import('./local-ai');
        let combinedMessage = agentRes.reasoning || "";
        let finalData = {};

        if (agentRes.toolCalls) {
          for (const call of agentRes.toolCalls) {
            console.log(`[🤖 Agent Loop] Executing Tool: ${call.toolName}`, call.params);
            const res = await TaskExecutor.execute(call.toolName, call.params);
            if (res.message) combinedMessage += `\n\n${res.message}`;
            finalData = { ...finalData, ...res.data };
          }
        }

        return {
          intent: nluResult.intent.name,
          response: combinedMessage,
          data: finalData,
          systemLog: `🤖 OS Bot · Agent`
        };
      }
    }

    let cloudRes: any = null;
    if (!isLocalMode) {
      console.log(`[OS Bot Pipeline] ☁️ Cloud Escalation: Calling ${effectiveModel}...`);
      cloudRes = await callExternalAPI(prompt, { 
        ...context, 
        modelId: effectiveModel,
        provider: effectiveModel,
        messages: context.messages || [] // Pass history
      }, signal);
    }

    const responseText = cloudRes?.response;
    const actualModel = cloudRes?.model || effectiveModel;

    // 3. FALLBACK TO LOCAL AI IF CLOUD FAILED
    if (!responseText && nluResult && nluResult.confidence >= 90) {
        console.log(`[🤖 OS BRAIN] Cloud unavailable/skipped. Falling back to Local Action:`, nluResult.intent.name);
        const executionResult = await TaskExecutor.execute(nluResult.intent.name, nluResult.params);
        const personality = store.aiTone || 'Professional';
        
        return {
          intent: nluResult.intent.name,
          response: generateResponse(nluResult.intent, executionResult, prompt, personality),
          data: executionResult.data,
          systemLog: `🤖 OS Bot · Local`
        };
    }

    // 4. FINAL RECOVERY IF EVERYTHING FAILED
    if (!responseText) {
      const recoveryMsg = isLocalMode 
        ? "I'm sorry, I couldn't understand that command locally. My local brain is still learning! Try asking for 'help' or switch to a cloud model in Settings for more advanced reasoning."
        : "I'm having trouble connecting to my external intelligence, and my local brain didn't quite catch that. Try saying 'help' to see what I can do locally!";
      
      return {
        intent: 'unknown',
        response: recoveryMsg,
        systemLog: `🤖 OS Bot · Local`
      };
    }

    const getProviderName = (m: any) => {
      const lower = String(m || '').toLowerCase();
      if (lower.includes('gemini')) return 'gemini';
      if (lower.includes('claude') || lower.includes('anthropic')) return 'claude';
      if (lower.includes('gpt') || lower.includes('openai')) return 'openai';
      if (lower.includes('deepseek')) return 'deepseek';
      if (lower.includes('groq')) return 'groq';
      if (lower.includes('together')) return 'together';
      if (lower.includes('minimax')) return 'minimax';
      if (lower.includes('mistral')) return 'mistral';
      if (lower.includes('cohere')) return 'cohere';
      if (lower === 'local' || lower === 'os-bot') return 'local';
      
      const parts = lower.split(/[-/_]/);
      const first = parts[0];
      if (['gemini', 'claude', 'gpt', 'openai', 'deepseek', 'groq', 'mistral', 'together', 'minimax'].includes(first)) {
        return first === 'gpt' ? 'openai' : first;
      }
      
      return first || m || 'bot';
    };

    let result: BotResponse = { 
      intent: cloudRes?.intent || 'unknown', 
      response: responseText, 
      systemLog: `🤖 OS Bot · ${getProviderName(cloudRes?.provider || actualModel)}`,
      data: cloudRes?.data || null
    };
    
    if (responseText.includes('{') && responseText.includes('}')) {
      try {
        // Sanitize responseText to extract clean JSON if it's wrapped in markdown blocks or contains conversational text
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("No JSON object found in response");
        const jsonStr = jsonMatch[0];
        const parsed = JSON.parse(jsonStr);
        
        result = {
          intent: parsed.intent || 'unknown',
          response: parsed.response || parsed.text || parsed.message || (parsed.intent ? `I'll handle that for you.` : responseText),
          data: parsed.data || parsed.params || null,
          systemLog: `🤖 OS Bot · ${getProviderName(cloudRes?.provider || actualModel)}`
        };

        // If the neural core requested an action, execute it locally
        if (result.intent !== 'unknown' && !['greeting', 'small_talk', 'joke'].includes(result.intent)) {
          console.log(`[🤖 OS BRAIN] Executing Neural Action: ${result.intent}`);
          const { TaskExecutor } = await import('./local-ai');
          const executionResult = await TaskExecutor.execute(result.intent, result.data || {});
          
          if (executionResult.success) {
            result.data = executionResult.data;
            
            // v13.5: Advanced response merging
            // If the LLM response is short/generic or it's a data-heavy intent like search_leads, 
            // we prioritize the executor's detailed results.
            const isPlaceholder = (result.response.length < 60 && (
              result.response.toLowerCase().includes('search') || 
              result.response.toLowerCase().includes('look') ||
              result.response.toLowerCase().includes('handle') ||
              result.response.toLowerCase().includes('get') ||
              result.response.toLowerCase().includes('okay')
            ));

            if (isPlaceholder || !parsed.response || result.intent === 'search_leads') {
              result.response = executionResult.message;
            } else if (executionResult.message && executionResult.message !== result.response) {
              // Append detailed execution results if we already have a natural phrasing
              result.response += `\n\n${executionResult.message}`;
            }
          }
        }
      } catch (e) { 
        console.warn('[🤖 OS BRAIN] Failed to parse neural action JSON fallback to raw text:', e);
        // Strip raw JSON from the response text so it never shows in chat
        result.response = responseText.replace(/```json[\s\S]*?```/gi, '').replace(/\{[\s\S]*"intent"[\s\S]*\}/i, '').trim();
        if (!result.response) result.response = "I encountered an error understanding that request.";
      }
    }

    // 3. THE TEACHING LOOP: If Smart Core found an intent the local missed, TEACH it.
    if (userId && result.intent !== 'unknown' && result.intent !== 'error' && (!nluResult || nluResult.confidence < 80)) {
       console.log(`[🤖 OS BRAIN] TEACHING MOMENT: "${prompt}" -> ${result.intent}`);
       const { saveLearnedIntent } = await import('./local-ai/learning-service');
       await saveLearnedIntent(userId, prompt, result.intent, result.data || {});
    }

    return result;
  } catch (error: any) {
    console.error('[🤖 OS BRAIN] Total Processing Failure:', error);
    return {
      intent: 'error',
      response: "My neural core encountered a total failure. I've logged the crash report. For now, try simpler commands.",
      systemLog: '🤖 OS Bot'
    };
  }
}

export function hasUserApiKey() {
  const store = useStore.getState();
  
  // If we're on local-only mode, we always have "permission" to use the bot
  if (store.currentUser?.preferred_api_provider === 'local' || store.preferred_api_provider === 'local') {
    return true;
  }

  // If user has credits, they can use the platform keys
  if ((store.currentUser?.credits_remaining || 0) > 0) {
    return true;
  }

  // Check profile keys in store or local fallback
  const profileKeys = store.currentUser?.user_api_keys;
  if (profileKeys && Object.keys(profileKeys).length > 0) return true;
  
  return !!(
    localStorage.getItem('user_gemini_api_key') ||
    localStorage.getItem('user_openai_api_key') ||
    localStorage.getItem('user_anthropic_api_key') ||
    localStorage.getItem('user_ai_api_key')
  );
}
